import { create } from 'zustand';
import type { NFTData, BannerStore, BannerNFTElement, BannerPreset } from '../types';
import { DEFAULT_BANNER_PRESET } from '../constants';
import { undoMiddleware } from './undoMiddleware';

function generateElementId(): string {
  return `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

import type { BannerState, BannerActions } from '../types';

type BannerBase = BannerState & BannerActions;

const BANNER_TRACKED_KEYS: (keyof BannerBase)[] = [
  'canvasWidth', 'canvasHeight', 'displayWidth', 'displayHeight',
  'activePreset', 'backgroundImage', 'backgroundColor', 'elements',
];

export const useBannerStore = create<BannerStore>(undoMiddleware<BannerBase>(BANNER_TRACKED_KEYS, (set, get) => ({
  canvasWidth: DEFAULT_BANNER_PRESET.width,
  canvasHeight: DEFAULT_BANNER_PRESET.height,
  displayWidth: DEFAULT_BANNER_PRESET.width,
  displayHeight: DEFAULT_BANNER_PRESET.height,
  activePreset: DEFAULT_BANNER_PRESET,
  backgroundImage: null,
  backgroundColor: '#ffffff',
  elements: [],
  selectedElementId: null,
  isDragging: false,
  isResizing: false,
  resizeHandle: null,

  setPreset: (preset: BannerPreset) => {
    set({
      activePreset: preset,
      canvasWidth: preset.width,
      canvasHeight: preset.height,
      displayWidth: preset.width,
      displayHeight: preset.height,
    });
  },

  setCustomDimensions: (width: number, height: number) => {
    set({
      canvasWidth: width,
      canvasHeight: height,
      displayWidth: width,
      displayHeight: height,
      activePreset: { id: 'custom', name: 'Custom', width, height },
    });
  },

  setDisplayDimensions: (width: number, height: number) => {
    set({ displayWidth: width, displayHeight: height });
  },

  setBackgroundImage: (imageUrl: string | null) => {
    set({ backgroundImage: imageUrl });
  },

  setBackgroundColor: (color: string) => {
    set({ backgroundColor: color });
  },

  addNFTElement: (nft: NFTData) => {
    const { elements, canvasWidth, canvasHeight } = get();
    const maxZIndex = elements.length > 0 ? Math.max(...elements.map((e) => e.zIndex)) : 0;

    const newElement: BannerNFTElement = {
      id: generateElementId(),
      nftData: nft,
      position: {
        x: canvasWidth / 2,
        y: canvasHeight / 2,
      },
      scale: 0.28,
      rotation: 0,
      zIndex: maxZIndex + 1,
      selectedLayers: [],
      useBackgroundLayer: false,
      useFrameLayer: false,
      backgroundVerticalOffset: 0,
      frameVerticalOffset: 0,
    };

    set({
      elements: [...elements, newElement],
      selectedElementId: newElement.id,
    });
  },

  removeElement: (elementId: string) => {
    const { elements, selectedElementId } = get();
    set({
      elements: elements.filter((e) => e.id !== elementId),
      selectedElementId: selectedElementId === elementId ? null : selectedElementId,
    });
  },

  duplicateElement: (elementId: string) => {
    const { elements } = get();
    const element = elements.find((e) => e.id === elementId);
    if (!element) return;

    const maxZIndex = Math.max(...elements.map((e) => e.zIndex));
    const newElement: BannerNFTElement = {
      ...element,
      id: generateElementId(),
      position: {
        x: element.position.x + 50,
        y: element.position.y + 50,
      },
      zIndex: maxZIndex + 1,
    };

    set({
      elements: [...elements, newElement],
      selectedElementId: newElement.id,
    });
  },

  updateElementPosition: (elementId: string, position: { x: number; y: number }) => {
    set({
      elements: get().elements.map((e) => (e.id === elementId ? { ...e, position } : e)),
    });
  },

  updateElementScale: (elementId: string, scale: number) => {
    set({
      elements: get().elements.map((e) => (e.id === elementId ? { ...e, scale } : e)),
    });
  },

  updateElementRotation: (elementId: string, rotation: number) => {
    set({
      elements: get().elements.map((e) => (e.id === elementId ? { ...e, rotation } : e)),
    });
  },

  updateElementZIndex: (elementId: string, zIndex: number) => {
    set({
      elements: get().elements.map((e) => (e.id === elementId ? { ...e, zIndex } : e)),
    });
  },

  reorderElements: (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;

    const { elements } = get();
    const orderedByFront = [...elements].sort((a, b) => b.zIndex - a.zIndex);
    const sourceIndex = orderedByFront.findIndex((e) => e.id === sourceId);
    const targetIndex = orderedByFront.findIndex((e) => e.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const [moved] = orderedByFront.splice(sourceIndex, 1);
    orderedByFront.splice(targetIndex, 0, moved);

    const sortedZValues = [...elements].map((e) => e.zIndex).sort((a, b) => a - b);
    const zIndexById = new Map<string, number>();
    orderedByFront.forEach((e, index) => {
      zIndexById.set(e.id, sortedZValues[sortedZValues.length - 1 - index]);
    });

    set({
      elements: elements.map((e) => ({
        ...e,
        zIndex: zIndexById.get(e.id) ?? e.zIndex,
      })),
    });
  },

  updateElementLayers: (
    elementId: string,
    selectedLayers: string[],
    useBackground: boolean,
    useFrame: boolean
  ) => {
    set({
      elements: get().elements.map((e) =>
        e.id === elementId
          ? { ...e, selectedLayers, useBackgroundLayer: useBackground, useFrameLayer: useFrame }
          : e
      ),
    });
  },

  updateElementVerticalOffsets: (
    elementId: string,
    backgroundOffset: number,
    frameOffset: number
  ) => {
    set({
      elements: get().elements.map((e) =>
        e.id === elementId
          ? { ...e, backgroundVerticalOffset: backgroundOffset, frameVerticalOffset: frameOffset }
          : e
      ),
    });
  },

  selectElement: (elementId: string | null) => {
    set({ selectedElementId: elementId });
  },

  bringToFront: (elementId: string) => {
    const { elements } = get();
    const maxZIndex = Math.max(...elements.map((e) => e.zIndex));
    set({
      elements: elements.map((e) => (e.id === elementId ? { ...e, zIndex: maxZIndex + 1 } : e)),
    });
  },

  sendToBack: (elementId: string) => {
    const { elements } = get();
    const minZIndex = Math.min(...elements.map((e) => e.zIndex));
    set({
      elements: elements.map((e) => (e.id === elementId ? { ...e, zIndex: minZIndex - 1 } : e)),
    });
  },

  resetCanvas: () => {
    const { elements } = get();
    const resetWidth = DEFAULT_BANNER_PRESET.width;
    const resetHeight = DEFAULT_BANNER_PRESET.height;

    // Keep NFT elements but reset their properties to defaults
    const resetElements = elements.map((e, index) => ({
      ...e,
      position: { x: resetWidth / 2, y: resetHeight / 2 },
      scale: 0.28,
      rotation: 0,
      zIndex: index + 1,
      selectedLayers: [],
      useBackgroundLayer: false,
      useFrameLayer: false,
      backgroundVerticalOffset: 0,
      frameVerticalOffset: 0,
    }));

    set({
      canvasWidth: resetWidth,
      canvasHeight: resetHeight,
      displayWidth: resetWidth,
      displayHeight: resetHeight,
      activePreset: DEFAULT_BANNER_PRESET,
      backgroundImage: null,
      backgroundColor: '#ffffff',
      elements: resetElements,
      selectedElementId: null,
      isDragging: false,
      isResizing: false,
      resizeHandle: null,
    });
    get()._clearHistory();
  },

  setDragging: (isDragging: boolean) => {
    if (isDragging) {
      get()._saveCheckpoint();
      get()._pauseUndo();
    }
    set({ isDragging });
    if (!isDragging) {
      get()._resumeUndo();
    }
  },

  setResizing: (isResizing: boolean, handle: string | null = null) => {
    if (isResizing) {
      get()._saveCheckpoint();
      get()._pauseUndo();
    }
    set({ isResizing, resizeHandle: handle });
    if (!isResizing) {
      get()._resumeUndo();
    }
  },
}), { selectedElementId: null } as Partial<BannerBase>));
